import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import VocabularySection, { type VocabTerm } from "./VocabularySection";
import BookInsightCard, { type BookInsight } from "./BookInsightCard";
import CompanyCaseBlock, { type LessonCompanyCase } from "./CompanyCaseBlock";
import VocabContrastCard from "./VocabContrastCard";
import VocabResurfaceCard from "./VocabResurfaceCard";
import CurrentExampleCard from "./CurrentExampleCard";
import { useVocabReinforcement } from "@/hooks/useVocabReinforcement";
import { useSfx } from "@/hooks/useSfx";

interface Props {
  lessonName: string;
  courseName?: string;
  vocabulary: VocabTerm[];
  bookInsights: BookInsight[];
  companyCases: LessonCompanyCase[];
  decisionPrompt?: string | null;
  onContinueToQuiz: () => void;
  onDecisionPrompt?: () => void;
  lessonData?: {
    current_example_title?: string | null;
    current_example_body?: string | null;
    current_example_why_it_matters?: string | null;
    example_date?: string | null;
    current_example_source_type?: string | null;
    is_current_example_active?: boolean;
  };
}

export default function MasteryLessonContent({
  lessonName,
  courseName,
  vocabulary,
  bookInsights,
  companyCases,
  decisionPrompt,
  onContinueToQuiz,
  onDecisionPrompt,
  lessonData,
}: Props) {
  const { contrastPairs, dueTerms, markReviewed } = useVocabReinforcement(courseName);
  const { playSfx } = useSfx();
  const hasContent = vocabulary.length > 0 || bookInsights.length > 0 || companyCases.length > 0;

  if (!hasContent && !lessonData?.current_example_title) return null;

  const primaryInsight = bookInsights.find((i) => i.is_primary);
  const secondaryInsights = bookInsights.filter((i) => !i.is_primary);

  const vocabTermNames = vocabulary.map(v => v.term.toLowerCase());
  const relevantContrasts = contrastPairs.filter(
    cp => vocabTermNames.some(t => cp.term_a.toLowerCase().includes(t) || cp.term_b.toLowerCase().includes(t))
  ).slice(0, 2);

  const effectiveDecisionPrompt = decisionPrompt;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/80 backdrop-blur-md border-b border-primary/10">
        <div className="max-w-5xl mx-auto px-3 sm:px-4 py-3">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0 flex-1">
              <p className="text-xs text-muted-foreground font-body uppercase tracking-wider">Lesson Insight</p>
              <h1 className="font-heading font-bold text-sm text-foreground truncate">{lessonName}</h1>
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-3 sm:px-4 py-6 pb-32 space-y-6 sm:space-y-8">
        {/* Current Example */}
        {lessonData?.current_example_title && lessonData?.current_example_body && lessonData?.is_current_example_active !== false && (
          <CurrentExampleCard
            title={lessonData.current_example_title}
            body={lessonData.current_example_body}
            whyItMatters={lessonData.current_example_why_it_matters}
            date={lessonData.example_date}
            sourceType={lessonData.current_example_source_type}
          />
        )}

        {/* Vocabulary */}
        {vocabulary.length > 0 && <VocabularySection terms={vocabulary} />}

        {/* Vocab Contrast Pairs */}
        {relevantContrasts.map(cp => (
          <VocabContrastCard
            key={cp.id}
            termA={cp.term_a}
            termB={cp.term_b}
            explanation={cp.explanation}
          />
        ))}

        {/* Spaced Vocabulary Resurfacing */}
        {dueTerms.length > 0 && (
          <section className="space-y-2">
            {dueTerms.slice(0, 2).map(dt => (
              <VocabResurfaceCard
                key={dt.term}
                term={dt.term}
                definition=""
                onReviewed={markReviewed}
              />
            ))}
          </section>
        )}

        {/* Primary Book Insight */}
        {primaryInsight && <BookInsightCard insight={primaryInsight} />}

        {/* Company Cases */}
        {companyCases.map((cc) => (
          <CompanyCaseBlock
            key={cc.id}
            companyCase={cc}
            onDecisionPromptClick={onDecisionPrompt}
          />
        ))}

        {/* Secondary Insights */}
        {secondaryInsights.map((insight) => (
          <BookInsightCard key={insight.id} insight={insight} />
        ))}
      </main>

      {/* Bottom CTA — bright holographic progression bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-background/90 backdrop-blur-md border-t border-[hsl(190_85%_60%/0.25)] p-3 sm:p-4">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[hsl(190_95%_65%/0.7)] to-transparent" />
        <div className="max-w-5xl mx-auto">
          <Button
            onClick={() => {
              playSfx("soft_swoosh");
              if (effectiveDecisionPrompt && onDecisionPrompt) {
                onDecisionPrompt();
              } else {
                onContinueToQuiz();
              }
            }}
            className="group relative w-full py-3 overflow-hidden border border-[hsl(280_85%_70%/0.45)] bg-gradient-to-r from-[hsl(225_75%_50%/0.55)] via-[hsl(280_75%_55%/0.55)] to-[hsl(330_80%_55%/0.55)] text-[hsl(310_95%_94%)] font-heading font-semibold hover:from-[hsl(225_85%_60%/0.7)] hover:via-[hsl(280_85%_65%/0.7)] hover:to-[hsl(330_90%_65%/0.7)] shadow-[0_0_28px_-8px_hsl(310_90%_60%/0.65)] gap-2"
          >
            <span className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent" />
            {effectiveDecisionPrompt && onDecisionPrompt ? "Continue to Decision Prompt" : "Continue to Quiz"}
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
